import { useState } from "react";
import { Field, Label } from "@headlessui/react";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import ModalForm from "../../components/ModalForm";
import RequiredField from "../../components/RequiredField";
import Password from "../../components/input/Password";
import { passwordInputClassName } from "./PasswordConfirmDialog";
import { SecretViewModel, useChangeSecretMutation } from "./secretsApiSlice";

interface Props {
	secret: SecretViewModel;
	cacheMinutes: number;
	onClose: () => void;
}

export default function ChangeSecretDialog({ secret, cacheMinutes, onClose }: Props) {
	const [newValue, setNewValue] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState("");

	const [changeSecret, { isLoading, reset }] = useChangeSecretMutation();

	function handleConfirm() {
		setError("");
		changeSecret({ secret: secret.name, newValue, password })
			.unwrap()
			.then(() => onClose())
			.catch((e: any) => {
				setPassword("");
				setError(typeof e?.data === "string" ? e.data : "Failed to change the secret.");
			})
			.finally(() => reset());
	}

	return (
		<ModalForm
			open
			title={`Change ${secret.name}`}
			form={
				<div className="pt-2 space-y-4">
					<AlertMessage
						type={AlertLevel.Error}
						message={error}
						showIcon
						onClear={() => setError("")}
					/>
					<p className="text-sm text-gray-600 mb-2">
						The new value is used straight away on this server, and within{" "}
						{cacheMinutes} minute{cacheMinutes === 1 ? "" : "s"} everywhere else.
						{secret.needsRestart &&
							" It only takes effect after the site restarts."}{" "}
						The current value is kept, so it can be restored.
					</p>
					<Field>
						<Label htmlFor="secrets-new-value" className="font-medium text-sm">
							New Secret
							<RequiredField />
						</Label>
						<Password
							id="secrets-new-value"
							name="secrets-new-value"
							placeholder="secret"
							autoComplete="new-password"
							className={passwordInputClassName}
							value={newValue}
							onChange={(e) => setNewValue(e.target.value)}
						/>
					</Field>
					<Field>
						<Label htmlFor="secrets-your-password" className="font-medium text-sm">
							Enter Your Password
							<RequiredField />
						</Label>
						<Password
							id="secrets-your-password"
							name="secrets-your-password"
							placeholder="your password"
							autoComplete="new-password"
							className={passwordInputClassName}
							value={password}
							onChange={(e) => setPassword(e.target.value)}
						/>
					</Field>
				</div>
			}
			confirmLabel="Change Secret"
			disableConfirm={!newValue.trim() || !password || isLoading}
			onConfirm={handleConfirm}
			onCancel={onClose}
		/>
	);
}
